import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

interface GuidanceSelectorProps {
  handleGuidanceTypeChange?: (value: boolean | null) => void;
  group_guidance_value?: boolean;
  variant?: 'standard' | 'outlined';
  loading?: boolean;
  disabled?: boolean;
  input_label?: string;
}

/**
 * Guidance Type Select auto-complete component to handle the guidance required for a new and edited group
 * @param {boolean | null} props.handleGuidanceTypeChange Callback function to notify the parent component when the selected options have changed.
 * @param {boolean} props.group_guidance_value Value recieved from parent of groups current guided level as a boolean when editing a group
 * @param {string} props.variant Variant of the text field to be displayed on the frontend
 * @param {boolean} props.loading A flag to set the autocomplete into a loading state for things such as fetching entries from database.
 * @param {boolean} props.disabled Renders the component in a disabled state along with any other provided components. Defaults to enabled.
 * @param {string} props.input_label Sets a custom component input label. Defaults to the singular version of the main entity name.
 */

const guidance_options = ['Guided', 'Unguided'];

const GuidanceSelect = (props: GuidanceSelectorProps) => {
  const [selectedType, setSelectedType] = React.useState<string | null>(null);

  const handleGuidanceChange = (selected_value: string | null) => {
    if (props.handleGuidanceTypeChange) {
      if (selected_value) {
        if (selected_value === guidance_options[0]) {
          props.handleGuidanceTypeChange(true);
          setSelectedType(selected_value);
        } else {
          props.handleGuidanceTypeChange(false);
          setSelectedType(selected_value);
        }
      } else {
        props.handleGuidanceTypeChange(null);
        setSelectedType(null);
      }
    } else {
      setSelectedType(selected_value);
    }
  };

  React.useEffect(() => {
    if (props.group_guidance_value) {
      setSelectedType(guidance_options[0]);
    } else if (props.group_guidance_value !== undefined) {
      setSelectedType(guidance_options[1]);
    }
  }, []);

  return (
    <Autocomplete
      id="guidance-type-select"
      options={guidance_options}
      autoHighlight
      disabled={props.disabled}
      loading={props.loading}
      value={selectedType}
      onChange={(event, value) => {
        handleGuidanceChange(value);
      }}
      isOptionEqualToValue={(option, value) => option === value}
      getOptionLabel={(option) => option}
      renderInput={(params) => (
        <TextField
          variant={props.variant ?? 'standard'}
          {...params}
          label={props.input_label ?? 'Guidance'}
          inputProps={{
            ...params.inputProps
          }}
        />
      )}
    />
  );
};

export default GuidanceSelect;
